import { useState } from "react";
import { useGetAvailablePickupSlots, usePostpone } from "@/api/postpone.api";
import { DatePicker } from "@mui/x-date-pickers";
import { GetAllExpeditionResponseDtoExpedition } from "@/types/api/api-types";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { Separator } from "../ui/separator";

type PostponeDialogProps = {
  expedition: GetAllExpeditionResponseDtoExpedition;
  onClose: () => void;
};

export const PostponeDialog = ({ expedition, onClose }: PostponeDialogProps) => {
  const [newPickupDate, setNewPickupDate] = useState<Date | null>(null);
  const { mutateAsync: postpone, isError, isPending, isSuccess } = usePostpone();
  const {
    data: availablePickupSlots,
    isLoading: isLoadingAvailablePickupSlots,
    isError: isErrorAvailablePickupSlots,
    isSuccess: isSuccessAvailablePickupSlots,
  } = useGetAvailablePickupSlots(expedition.id);

  const handlePostpone = async () => {
    if (!newPickupDate) return;
    await postpone({
      id: expedition.id,
      payload: {
        token: "",
        pickupDate: new Date(new Date(newPickupDate).setHours(12, 0, 0, 0)).toISOString(),
      },
    });
  };

  const shouldDisableDate = (date: Date) => {
    const dateString = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
    if (availablePickupSlots.length === 0) return false;
    return availablePickupSlots.some(
      (el: { date: string; slot: string }) => el.date.split("T")[0] === dateString,
    );
  };

  if (isLoadingAvailablePickupSlots) {
    return (
      <dialog
        onClick={onClose}
        className="fixed top-0 left-0 z-50 flex h-full w-full items-center justify-center overflow-auto bg-[#00000080] backdrop-blur-sm"
      >
        <div
          className="mx-4 flex max-w-[600px] flex-col items-center gap-4 rounded-2xl bg-white"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="relative flex w-full flex-col items-center">
            {isSuccess ? (
              <h2 className="p-4 text-center text-2xl font-semibold text-black">
                L'enlèvement a bien été reporté
              </h2>
            ) : isError ? (
              <h2 className="p-4 text-center text-2xl font-semibold text-black">
                Nous n'avons pas pu reporter l'enlèvement.
              </h2>
            ) : (
              <h2 className="p-4 text-center text-2xl font-semibold text-black">
                Reporter l'enlèvement
              </h2>
            )}
            <Separator />

            <div className="flex flex-col items-center gap-3 p-4">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            </div>
          </div>
        </div>
      </dialog>
    );
  }

  return (
    <dialog
      onClick={onClose}
      className="fixed top-0 left-0 z-50 flex h-full w-full items-center justify-center overflow-auto bg-[#00000080] backdrop-blur-sm"
    >
      <div
        className="mx-4 flex max-w-[600px] flex-col items-center gap-4 rounded-2xl bg-white"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative flex w-full flex-col items-center">
          {isSuccess ? (
            <h2 className="p-4 text-center text-2xl font-semibold text-black">
              L'enlèvement a bien été reporté
            </h2>
          ) : isError ? (
            <h2 className="p-4 text-center text-2xl font-semibold text-black">
              Nous n'avons pas pu reporter l'enlèvement.
            </h2>
          ) : (
            <h2 className="p-4 text-center text-2xl font-semibold text-black">
              Reporter l'enlèvement
            </h2>
          )}
          <Separator />
          <div className="flex flex-col items-center gap-3 p-4">
            {isError ? (
              <>
                <h1 className="w-full text-center text-lg font-medium text-black sm:text-xl">
                  Nous n'avons pas pu reporter l'enlèvement.
                </h1>
                <p className="text-start text-sm text-red-800">
                  Aucun créneau disponible n’a pu être trouvé. Merci de contacter notre équipe pour
                  reprogrammer l’enlèvement.
                </p>
                <Button onClick={onClose} type="button" variant="secondary">
                  Fermer
                </Button>
              </>
            ) : isSuccess ? (
              <>
                <p className="text-start text-sm text-gray-600">
                  Vous allez recevoir un mail avec la nouvelle date d'enlèvement et un nouveau lien
                  pour reporter l'enlèvement si nécessaire.
                </p>
                <Button onClick={onClose} type="button" variant="secondary">
                  Fermer
                </Button>
              </>
            ) : isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <>
                <span className="w-full text-center text-base font-medium text-black">
                  Souhaitez-vous reprogrammer l’enlèvement à une autre date ?
                </span>
                <span className="w-full max-w-[80%] text-center text-sm font-medium text-gray-600">
                  Sélectionnez la nouvelle date souhaitée (dans le calendrier ci-dessous)
                </span>
                <DatePicker
                  disabled={isLoadingAvailablePickupSlots || isErrorAvailablePickupSlots}
                  value={newPickupDate}
                  onChange={(date) => setNewPickupDate(date)}
                  disablePast
                  shouldDisableDate={(date) => !shouldDisableDate(date)}
                />
                {isSuccessAvailablePickupSlots && availablePickupSlots.length === 0 && (
                  <span className="text-center text-sm text-red-800">
                    Il semblerait que nous n'ayont pas réussi à trouver de créneau disponible.
                    Veuillez nous contacter pour reporter l'enlèvement.
                  </span>
                )}
                <div className="flex w-fit flex-col items-stretch gap-2 pt-2 md:flex-row-reverse">
                  <Button disabled={!newPickupDate} type="button" onClick={handlePostpone}>
                    {isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : "Reporter"}
                  </Button>
                  <Button onClick={onClose} type="button" variant="secondary">
                    Annuler
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </dialog>
  );
};
